import { effect, inject, Injectable, NgZone, signal } from '@angular/core';
import { environment } from '@nomoh-monorepo/shared-data-access-environment';
import {
  IWhatsappConfig,
  IWhatsappLogEntry,
} from '@nomoh-monorepo/shared-types';
import { io, Socket } from 'socket.io-client';
import { EstabelecimentoDataService } from '../feature-estabelecimento/estabelecimento.dataservice';

@Injectable({
  providedIn: 'root',
})
export class WhatsappConfigDataService {
  //#region injections
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private ngZone = inject(NgZone);

  //#endregion
  //#region  signals
  private _whatsappConfig = signal<IWhatsappConfig>({
    habilitado: false,
    status: 'desconectado',
    fluxoAtendimentoId: null,
    pairingCode: '',
  });

  $whatsappConfig = this._whatsappConfig.asReadonly();
  setWhatsappConfig(config: IWhatsappConfig) {
    this._whatsappConfig.set(config);
  }
  updateWhatsappConfig(config: IWhatsappConfig) {
    this._whatsappConfig.update(
      (cfg) =>
      ({
        ...cfg,
        ...config,
      } as IWhatsappConfig)
    );
  }

  private _socketInstanceName = signal<string | null>(null);
  $socketInstanceName = this._socketInstanceName.asReadonly();

  private _sessionName = signal<string>('');
  $sessionName = this._sessionName.asReadonly();

  private _loading = signal<boolean>(false);
  $loading = this._loading.asReadonly();
  setLoading(loading: boolean) {
    this._loading.set(loading);
  }
  private _error = signal<string | null>(null);
  $error = this._error.asReadonly();
  setError(error: string | null) {
    this._error.set(error);
  }

  private _logs = signal<IWhatsappLogEntry[]>([]);
  $logs = this._logs.asReadonly();

  addLogs(entry: IWhatsappLogEntry) {
    this._logs.update((logs) => [...logs, entry]);
  }

  private _whatsappConectado = signal<boolean>(false);
  $whatsappConectado = this._whatsappConectado.asReadonly();

  private _whatsappAuth = signal<boolean>(false);
  $whatsappAuth = this._whatsappAuth.asReadonly();

  setWhatsappConectado(conectado: boolean) {
    this._whatsappConectado.set(conectado);
  }

  //#region signals para controle do QR Code e pair code
  private _podeGerarCode = signal<boolean>(true);
  $podeGerarCode = this._podeGerarCode.asReadonly();
  private _qrCodeB64 = signal<string | undefined>(undefined);
  $qrCodeB64 = this._qrCodeB64.asReadonly();
  private _codeExpired = signal<boolean>(false);
  $codeExpired = this._codeExpired.asReadonly();

  private _pairingCodeGenerated = signal<string | null>(null);
  $pairingCodeGenerated = this._pairingCodeGenerated.asReadonly();
  //#endregion

  //#region signals para controle do countdown
  private _whatsappQrCountDownms = signal<number>(0);
  private _tempoRestanteCountdown = signal<number>(0);
  $tempoRestanteCountdown = this._tempoRestanteCountdown.asReadonly();

  //#endregion

  //#endregion

  //#region Variaveis
  private ultimaGeracaoQr: number | null = null;

  //#endregion

  //#region Variáveis para controle do Socket.IO
  private socket: Socket | null = null;
  private cooldownInterval: ReturnType<typeof setInterval> | null = null;
  //#endregion

  //#region Constantes
  private readonly QR_COOLDOWN_MS = 60000; // 60 segundos
  private readonly QR_VALIDITY_MS = 55000; // 55 segundos (tempo que QR Code fica válido)

  //#endregion

  constructor() {
    effect(() => {
      const estabelecimento =
        this.estabelecimentoDataService.$estabelecimentoAtual();
      if (estabelecimento && !this._sessionName()) {
        this._sessionName.set(
          this.buildSessionName(
            estabelecimento?.nome,
            estabelecimento?.fluxosAtendimento?.[0]?.id ?? null
          )
        );
        this.conectarSocket();
      }
    });
  }

  init(): void {
    return;
  }

  garantirSocketConectado(): void {
    if (this.socket?.connected) {
      return;
    }

    this.reconectarSocket();
  }

  prepararParaReconexao(): void {
    this.resetarEstadoConexao();
    this.reconectarSocket();
  }

  resetarEstadoConexao(): void {
    this._whatsappConectado.set(false);
    this._whatsappAuth.set(false);
    this._codeExpired.set(false);
    this.ultimaGeracaoQr = null;
    this.limparCountdown();
  }

  reconectarSocket(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    if (this._sessionName()) {
      this.conectarSocket();
    }
  }

  conectarSocket(): void {
    if (this.socket?.connected) {
      return;
    }

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.socket = io(this.resolveSocketUrl(), {
      transports: ['websocket'],
      query: { instanceName: this._sessionName() },
    });
    this.registrarEventosSocket(this.socket);
  }

  private registrarEventosSocket(socket: Socket): void {
    socket.on('qrCode', (qrCode: string) => {
      this.ngZone.run(() => {
        if (this._podeGerarCode()) {
          this._pairingCodeGenerated.set(null);
          this._loading.set(false);
          this._qrCodeB64.set(this.normalizarQrCode(qrCode));
          this.marcarGeracaoCode();
        }
      });
    });

    socket.on('pairingCode', (code: string) => {
      this.ngZone.run(() => {
        this._qrCodeB64.set(undefined); // Limpar QR Code se um pairing code for gerado
        this._pairingCodeGenerated.set(code);
        this._loading.set(false);
        this.marcarGeracaoCode(); // Marcar timestamp apenas quando Pairing Code é RECEBIDO
      });
    });

    socket.on('statusConexao', (status: string) => {
      this.ngZone.run(() => {
        this.processarStatusConexao(status);
      });
    });

    socket.on('disconnect', () => {
      this.ngZone.run(() => {
        this.socket = null;
      });
    });
  }

  private processarStatusConexao(status: string): void {
    const normalized = status.toLowerCase();

    if (['autoclosecalled'].includes(normalized)) {
      this._whatsappConectado.set(false);
      this._codeExpired.set(true);
      this._whatsappAuth.set(false);
      this._whatsappConfig.update((cfg) => ({ ...cfg, status: 'qrExpirado' }));
      this.limparCountdown();
    } else if (['connected'].includes(normalized)) {
      this._whatsappConectado.set(true);
      this._whatsappAuth.set(true);
      this._whatsappConfig.update((cfg) => ({ ...cfg, status: 'conectado' }));
      this.limparCountdown();
    } else if (['notlogged'].includes(normalized)) {
      if (this.$whatsappConectado()) {
        const config = {
          ...this.$whatsappConfig(),
          habilitado: false,
          telefone: undefined,
          ultimaConexao: undefined,
          status: 'desconectado',
        } as IWhatsappConfig;
        this.updateWhatsappConfig(config);
        this._whatsappConectado.set(false);
        this.limparCountdown();
      } else {
        this._whatsappAuth.set(false);
      }
    }
  }

  desconectarSocket(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.socket = null;
  }

  //#region Cooldown e Expiração do QR Code

  private marcarGeracaoCode(): void {
    const agora = Date.now();
    this.ultimaGeracaoQr = agora;
    this._podeGerarCode.set(false);
    this._codeExpired.set(false);
    this._whatsappQrCountDownms.set(agora);
    this.iniciarCountdown();
  }

  private iniciarCountdown(): void {
    if (this._tempoRestanteCountdown() > 0) {
      return;
    }

    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }

    this.cooldownInterval = setInterval(() => {
      const ultimaGeracao = this.ultimaGeracaoQr;
      if (!ultimaGeracao) {
        this.limparCountdown();
        return;
      }

      const agora = Date.now();
      const decorrido = agora - ultimaGeracao;
      const restante = Math.ceil((this.QR_COOLDOWN_MS - decorrido) / 1000);

      if (restante <= 0) {
        this.limparCountdown();
      } else {
        this._tempoRestanteCountdown.set(restante);
      }
    }, 1000);
  }

  private limparCountdown(): void {
    this._podeGerarCode.set(true);
    this._loading.set(false);
    this._tempoRestanteCountdown.set(0);
    this._whatsappQrCountDownms.set(0);
    this._qrCodeB64.set(undefined);
    this._pairingCodeGenerated.set(null);

    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
      this.cooldownInterval = null;
    }
  }

  //#endregion

  //#region  utilities
  private buildSessionName(
    nomeEstabelecimento: string | undefined,
    fluxoId: string | null
  ): string {
    const nameSlug =
      nomeEstabelecimento
        ?.normalize('NFD')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase() ?? 'estabelecimento';

    return `${nameSlug}-${fluxoId}`;
  }

  private resolveSocketUrl(): string {
    const zapApiUrl = environment.apiUrls?.zap;
    if (!zapApiUrl) {
      return 'http://localhost:3003';
    }

    try {
      const url = new URL(zapApiUrl);
      return `${url.protocol}//${url.host}`;
    } catch (error) {
      console.warn(
        'Não foi possível interpretar apiUrls.zap; usando fallback.',
        error
      );
      return 'http://localhost:3003';
    }
  }

  private normalizarQrCode(valor: string): string {
    return valor.startsWith('data:') ? valor : `data:image/png;base64,${valor}`;
  }
  //#endregion
}
